<template>
  <Transition name="modal">
    <div v-if="show" class="relative z-10" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-start justify-center p-4 text-center sm:items-center sm:p-0">
          <div
              class="modal-container relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg md:max-w-[1200px]"
          >
            <div class="relative bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">

              <i class="fa fa-times cursor-pointer absolute top-4 right-4" @click="close()"></i>
              <h3 class="text-secondary-500 mb-4 font-semibold"><span v-if="question.id">#{{ question.id }}</span>
                {{ getModalTitle }}</h3>

              <alert v-if="showUpdateAlert" type="success">{{ __('question.update_success') }}</alert>

              <div class="sm:flex sm:items-start">
                <div class="w-full grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div class="column">

                    <div class="flex flex-col justify-content-end mb-2">
                      <label for="possible_users"
                             class="block mb-2 text-sm font-medium text-secondary-500"
                      >
                        Frage
                      </label>
                      <textarea id="text" v-model="question.text" class="textarea" rows="5"
                                :placeholder="__('question.text')"
                                @update:modelValue="onChange"
                      ></textarea>
                    </div>

                    <div class="mb-5">
                      <label for="publish" class="block mb-2 text-sm font-medium text-secondary-500">Art
                        der Frage</label>
                      <Multiselect
                          v-model="question.type"
                          mode="single"
                          :attr="{id}"
                          :close-on-select="true"
                          :searchable="true"
                          :create-option="false"
                          :options="questionTypes"
                          @select="onChange"
                      />
                    </div>

                    <div class="mb-5">
                      <label for="publish" class="block mb-2 text-sm font-medium text-secondary-500">Kategorie</label>
                      <Multiselect
                          v-model="question.category_id"
                          mode="single"
                          :attr="{id}"
                          :close-on-select="true"
                          :searchable="true"
                          :create-option="false"
                          :options="categories"
                          @select="onChange"
                      />
                    </div>

                    <label for="hasDuplicate"
                           class="mt-1 inline-flex relative items-center cursor-pointer mb-4 lg:mb-0"
                    >
                      <input v-model="hasDuplicate" @change="onDuplicateChange" type="checkbox" id="hasDuplicate"
                             class="sr-only peer"
                      >
                      <div :class="toggleClass"
                           class=" w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"
                      ></div>
                      <span class="ml-3 text-sm font-medium text-gray-900">Doppelte Frage?</span>
                    </label>
                    <p class="text-sm italic text-gray-500 mb-2">Wenn diese Frage als doppelt markiert wird, wird diese
                      Frage-Variante aus den Übungen ausgefiltert.</p>

                    <div v-if="hasDuplicate" class="flex flex-col justify-content-end mb-2">
                      <label for="question_id"
                             class="block mb-2 text-sm font-medium text-secondary-500"
                      >
                        Fragen ID des Originals
                      </label>
                      <input type="text" id="question_id" v-model="question.is_duplicate" class="textarea"
                             placeholder="Im Zweifel 9999 eintragen"
                      />
                    </div>

                    <label for="hasContext"
                           class="mt-1 inline-flex relative items-center cursor-pointer mb-4 lg:mb-0"
                    >
                      <input v-model="hasContext" @change="onChange" type="checkbox" id="hasContext"
                             class="sr-only peer"
                      >
                      <div :class="toggleContextClass"
                           class=" w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"
                      ></div>
                      <span class="ml-3 text-sm font-medium text-gray-900">{{__('question.admin.is_context_label')}}</span>
                    </label>
                    <div v-if="hasContext" class="flex flex-col justify-content-end mb-2">
                      <label for="context" class="block mb-2 text-sm font-medium text-secondary-500"
                      >{{__('question.admin.context_text_label')}}</label>
                      <textarea id="context" v-model="question.context" class="textarea" rows="5"
                                :placeholder="__('question.admin.context')" @update:modelValue="onContextChange"
                      ></textarea>
                    </div>
                  </div>

                  <div class="column">
                    <div class="">
                      <label for="publish" class="block mb-2 text-sm font-medium text-secondary-500">Antwortmöglichkeiten</label>
                      <alert v-if="!question.choices || question.choices.length === 0" type="warning"><i
                          class="fa fa-info mr-2"
                      ></i> Es wurde noch keine Antwortmöglichkeiten angelegt
                      </alert>
                      <question-choices-item
                          v-for="(choice, index) in question.choices"
                          v-else
                          :key="index" :index="index" :choice="choice"
                          @was-changed="onChange"
                          @remove="removeChoice"
                      ></question-choices-item>
                      <button v-if="showAddChoiceBtn && question.id" class="btn-primary w-full" @click="addChoice"><i
                          class="fa fa-plus"
                      ></i> {{ __('choice.add') }}
                      </button>
                      <alert v-else-if="showAddChoiceBtn" type="success">Bitte Frage zuerst speichern.</alert>
                      <alert v-else type="info"><i class="fa fa-info mr-2"></i> Geben Sie hier die richtige Lösung ein.
                      </alert>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse flex justify-between gap-4 sm:px-6">
              <button v-if="showUpdateButton" type="button" :class="btnClass"
                      class="block w-full md:w-auto md:inline-flex justify-center text-sm md:text-base btn ml-2"
                      @click="saveQuestion"
              >{{ __('question.save') }}
              </button>
              <div class="btn group">
                <button type="button" @click="deleteQuestion()"
                        class="block w-full md:w-auto md:inline-flex justify-center text-sm md:text-base btn btn-danger ml-2 px-4 py-2"
                ><i class="fa fa-trash mr-2 mt-1"></i> {{ __('general.delete') }}
                </button>
                <button type="button" @click="close()"
                        class="hidden md:inline-flex justify-center rounded-md border text-sm md:text-base border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >{{ __('general.close') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import Multiselect from '@vueform/multiselect'
import {postQuestion, updateQuestion, deleteQuestion} from '../../api/QuestionApi'
import QuestionChoicesItem from './QuestionChoicesItem'

export default {

  components: {
    Multiselect,
    QuestionChoicesItem,
  },

  props: {
    show: Boolean,
    question: {
      type: Object,
      required: true,
    },
    categories: {
      type: Array,
      required: true,
    },
    btnClass: {
      type: String,
      required: false,
      default: 'btn-primary',
    },
  },

  emits: ['close', 'question-updated', 'question-created', 'question-deleted'],

  data() {
    return {
      finishedQuestion: null,
      showUpdateButton: false,
      showUpdateAlert: false,
      hasDuplicate: false,
      hasContext: false,
      questionTypes: [
        {
          value: 1,
          label: this.__('question.type.1'),
        },
        {
          value: 2,
          label: this.__('question.type.2'),
        },
        {
          value: 3,
          label: this.__('question.type.3'),
        },
      ],
    }
  },

  updated: function () {
    if (!this.question) {
      return
    }

    this.hasContext = (this.question.context !== '' && this.question.context !== null && this.question.context !== undefined);
    this.hasDuplicate = this.question.is_duplicate !== 0 && this.question.is_duplicate !== null && this.question.is_duplicate !== undefined
  },

  computed: {
    getModalTitle() {
      return (this.question.id) ? 'Frage bearbeiten' : 'Frage erstellen'
    },

    showAddChoiceBtn() {
      if (this.question.type === 3) {
        return (!this.question.choices || this.question.choices.length === 0)
      } else {
        return (this.question.type)
      }
    },

    toggleClass: function () {
      return (this.hasDuplicate) ? 'bg-secondary-500' : 'bg-gray-200'
    },

    toggleContextClass: function () {
      return (this.hasContext) ? 'bg-secondary-500' : 'bg-gray-200'
    },
  },

  methods: {
    close: function () {
      this.$emit('close')
    },

    saveQuestion: async function () {
      if (this.question.id) {
        this.finishedQuestion = await updateQuestion(this.question.id, this.question)
        this.$emit('question-updated', this.finishedQuestion)
      } else {
        // eslint-disable-next-line vue/no-mutating-props
        this.finishedQuestion = await postQuestion(this.question)
        this.$emit('question-created', this.finishedQuestion)
      }
    },

    addChoice: function () {
      // eslint-disable-next-line vue/no-mutating-props
      this.question.choices.push({
        is_correct: (this.question.type === 3),
        text: '',
        question_id: this.question.id,
      })
      this.onChange()
    },

    onChange: function () {
      if (this.question.text
          && this.question.type
          && this.question.category_id
          && (!this.hasContext || (this.hasContext && '' !== this.question.context ))) {
        this.showUpdateButton = true
      } else {
        console.log(this.question.text)
        console.log(this.question.type)
        console.log(this.question.category_id)
        this.showUpdateButton = false
      }
    },

    removeChoice: function (index) {
      // eslint-disable-next-line vue/no-mutating-props
      this.question.choices.splice(index, 1)
      this.onChange()
    },

    onDuplicateChange: function () {
      if (this.hasDuplicate && this.question.is_duplicate === null) {
        // eslint-disable-next-line vue/no-mutating-props
        this.question.is_duplicate = 9999
      } else if (!this.hasDuplicate) {
        // eslint-disable-next-line vue/no-mutating-props
        this.question.is_duplicate = null
      }
      this.onChange()
    },

    onHasContextChange: function () {
      if (!this.hasContext) {
        // eslint-disable-next-line vue/no-mutating-props
        this.question.context = ''
      }
      this.onChange()
    },

    deleteQuestion: async function () {
      if (!this.question.id) {
        return
      }
      if (confirm('Wollen Sie die Frage wirklich löschen?')) {
        //this.$emit('question-deleted', this.question.id)
        var result = await deleteQuestion(this.question.id)
        if (result) {
          this.$emit('question-deleted', this.question.id)
        }
      }
    },
  },
}
</script>

<style>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: table;
  transition: opacity 0.3s ease;
}

.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
}

.modal-header h3 {
  margin-top: 0;
  color: #42b983;
}

.modal-body {
  margin: 20px 0;
}

.modal-default-button {
  float: right;
}

/*
 * The following styles are auto-applied to elements with
 * transition="modal" when their visibility is toggled
 * by Vue.js.
 *
 * You can easily play with the modal transition by editing
 * these styles.
 */

.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}
</style>